import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Stack,
  Chip,
  alpha,
  Divider,
  Link,
  IconButton,
  Menu,
  MenuItem
} from '@mui/material';
import {
  CheckCircle,
  ArrowForward,
  EmojiPeople,
  SupportAgent,
  Favorite,
  InfoOutlined,
  RemoveCircleOutline,
  AutoAwesome,
  Menu as MenuIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import facteioLogo from '../assets/Facteio nouveau Logo.png';
import sebPortrait from '../assets/images/portrait_professionnel_seb_1.png';
import sebPortraitNew from '../assets/images/img profil Seb.png';

// Styles personnalisés
const HeroSection = styled(Box)(({ theme }) => ({
  minHeight: '90vh',
  background: `linear-gradient(135deg,
    ${alpha(theme.palette.primary.light, 0.26)} 0%,
    ${alpha(theme.palette.primary.main, 0.22)} 55%,
    ${alpha(theme.palette.secondary.main, 0.28)} 100%)`,
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  paddingTop: theme.spacing(8),
  paddingBottom: theme.spacing(8),
}));

const ZenButton = styled(Button)(({ theme, variant }) => ({
  borderRadius: 50,
  padding: '16px 48px',
  fontSize: '1.1rem',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: variant === 'contained' ? theme.shadows[4] : 'none',

  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: variant === 'contained' ? theme.shadows[6] : 'none',
  },

  transition: 'all 0.3s ease',
}));

const ProblemCard = styled(Card)(({ theme }) => ({
  background: alpha(theme.palette.background.paper, 0.92),
  border: `2px solid ${alpha(theme.palette.divider, 0.82)}`,
  borderRadius: 16,
  padding: theme.spacing(3),
  boxShadow: '0px 14px 34px rgba(79, 71, 65, 0.10)',
}));

const SolutionCard = styled(Card)(({ theme }) => ({
  background: alpha(theme.palette.background.paper, 0.94),
  border: `2px solid ${alpha(theme.palette.divider, 0.86)}`,
  borderRadius: 16,
  padding: theme.spacing(3),
  boxShadow: '0px 14px 34px rgba(79, 71, 65, 0.08)',
}));

const FeatureBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(2),
  background: alpha(theme.palette.background.paper, 0.66),
  borderRadius: 12,
  border: `1px solid ${alpha(theme.palette.divider, 0.78)}`,
  transition: 'all 0.3s ease',

  '&:hover': {
    background: alpha(theme.palette.primary.main, 0.14),
    transform: 'translateX(8px)',
  },
}));

const LandingNew = () => {
  const navigate = useNavigate();
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const isMobileMenuOpen = Boolean(mobileMenuAnchor);
  const [portraitSrc, setPortraitSrc] = useState(sebPortraitNew);

  useEffect(() => {
    document.title = 'Facteio – Logiciel devis et facture pour artisans, auto-entrepreneurs et PME';
    const description = 'Logiciel devis facture simple pour artisans, auto-entrepreneurs et PME, conforme à la facture électronique 2026. Essai gratuit, sans engagement.';
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', description);

    let metaKw = document.querySelector('meta[name="keywords"]');
    if (!metaKw) { metaKw = document.createElement('meta'); metaKw.setAttribute('name', 'keywords'); document.head.appendChild(metaKw); }
    metaKw.setAttribute('content', 'logiciel devis facture, logiciel facturation artisan, logiciel facture auto-entrepreneur, logiciel facturation PME, facture électronique 2026');

    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) { ogTitle = document.createElement('meta'); ogTitle.setAttribute('property', 'og:title'); document.head.appendChild(ogTitle); }
    ogTitle.setAttribute('content', 'Facteio – Logiciel devis et facture pour artisans, auto-entrepreneurs et PME');

    let ogDesc = document.querySelector('meta[property="og:description"]');
    if (!ogDesc) { ogDesc = document.createElement('meta'); ogDesc.setAttribute('property', 'og:description'); document.head.appendChild(ogDesc); }
    ogDesc.setAttribute('content', description);
  }, []);

  const goToRegisterTracked = (content = 'cta') => {
    const params = new URLSearchParams({
      utm_source: 'site_facteio',
      utm_medium: 'cta',
      utm_campaign: 'landing_new',
      utm_content: content
    });
    navigate(`/register?${params.toString()}`);
  };

  const handleOpenMobileMenu = (event) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleCloseMobileMenu = () => {
    setMobileMenuAnchor(null);
  };

  return (
    <Box sx={(theme) => ({ background: theme.palette.background.default, minHeight: '100vh' })}>
      {/* HEADER / NAV BAR */}
      <Box sx={{
        backgroundColor: (theme) => theme.palette.background.default,
        borderBottom: 'none',
        boxShadow: 'none',
        py: 2
      }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'auto 1fr', md: 'auto 1fr auto' },
              alignItems: 'center',
              gap: 2
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box
                component="img"
                src={facteioLogo}
                alt="Facteio"
                sx={{ height: { xs: 80, md: 120 }, width: 'auto' }}
              />
            </Box>

            <Stack
              direction="row"
              spacing={2}
              sx={{
                justifyContent: { xs: 'flex-end', md: 'flex-end' },
                display: { xs: 'none', md: 'flex' }
              }}
            >
              <Button sx={{ color: 'text.primary' }} onClick={() => navigate('/reforme-2026')}>Réforme 2026</Button>
              <Button sx={{ color: 'text.primary' }} onClick={() => navigate('/faq')}>FAQ</Button>
              <Button sx={{ color: 'text.primary' }} onClick={() => navigate('/pricing')}>Tarifs</Button>
              <Button variant="outlined" sx={{ borderColor: 'divider', color: 'text.primary' }} onClick={() => navigate('/login')}>
                Connexion
              </Button>
              <ZenButton
                variant="contained"
                sx={{
                  color: '#fff'
                }}
                onClick={() => goToRegisterTracked('header_desktop')}
              >
                Essayer gratuitement
              </ZenButton>
            </Stack>

            <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'flex-end' }}>
              <IconButton
                aria-label="menu"
                onClick={handleOpenMobileMenu}
                sx={{ color: 'text.primary' }}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={mobileMenuAnchor}
                open={isMobileMenuOpen}
                onClose={handleCloseMobileMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={() => { handleCloseMobileMenu(); navigate('/reforme-2026'); }}>Réforme 2026</MenuItem>
                <MenuItem onClick={() => { handleCloseMobileMenu(); navigate('/faq'); }}>FAQ</MenuItem>
                <MenuItem onClick={() => { handleCloseMobileMenu(); navigate('/pricing'); }}>Tarifs</MenuItem>
                <MenuItem onClick={() => { handleCloseMobileMenu(); navigate('/login'); }}>Connexion</MenuItem>
                <MenuItem onClick={() => { handleCloseMobileMenu(); goToRegisterTracked('header_mobile'); }}>Essayer gratuitement</MenuItem>
              </Menu>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* 🎯 HERO SECTION - TITRE PRINCIPAL */}
      <HeroSection>
        <Container maxWidth="lg">
          <Box textAlign="center">
            <Typography
              variant="h3"
              sx={(theme) => ({
                fontWeight: 900,
                mb: 3,
                fontSize: { xs: '4.5rem', md: '6rem' },
                background: `linear-gradient(135deg, ${alpha('#ffd700', 1)} 0%, ${alpha('#c5a643', 0.65)} 100%)`,
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                color: 'transparent',
                textShadow: [
                  // Extrusion (net, bas/droite)
                  `1px 1px 0 ${alpha(theme.palette.secondary.main, 0.20)}`,
                  `2px 2px 0 ${alpha(theme.palette.secondary.main, 0.14)}`,
                  `3px 3px 0 ${alpha(theme.palette.secondary.main, 0.10)}`,
                  // Ombre portée (moins "brouillard")
                  `0px 10px 22px ${alpha(theme.palette.secondary.main, 0.18)}`,
                ].join(', '),
              })}
            >
              Facteio
            </Typography>

            <Chip
              icon={<AutoAwesome />}
              label="Conforme réforme 2026"
              sx={{
                mb: 3,
                py: 2.5,
                fontSize: '1rem',
                fontWeight: 700,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.14),
                color: 'text.primary',
              }}
            />
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2rem', md: '3rem', lg: '3rem' },
                fontWeight: 900,
                mb: 2,
                color: 'text.primary',
                lineHeight: 1.2
              }}
            >
              La facture électronique 2026,<br />
              <Box component="span" sx={{ color: 'text.primary' }}>sans stress.</Box>
            </Typography>

            <Typography
              variant="h4"
              sx={{
                fontSize: { xs: '1.2rem', md: '1.5rem' },
                color: 'text.secondary',
                mb: 4,
                maxWidth: '800px',
                mx: 'auto',
                fontWeight: 400
              }}
            >
              Conçu pour les artisans, adapté aux petites entreprises.
            </Typography>

            {/* SOUS-TITRE */}
            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: '1rem', md: '1.25rem' },
                color: 'text.secondary',
                mb: 5,
                maxWidth: '700px',
                mx: 'auto',
                lineHeight: 1.6
              }}
            >
              Facteio est un <strong>logiciel devis facture artisan</strong> (bâtiment & métiers)<br />
              : simple, humain, prêt pour 2026, sans se noyer dans la technique.
            </Typography>

            {/* BOUTONS */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center" sx={{ mb: 5 }}>
              <ZenButton
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                sx={{
                  color: '#fff'
                }}
                onClick={() => navigate('/reforme-2026')}
              >
                Découvrir comment être prêt pour 2026
              </ZenButton>
              <ZenButton
                variant="outlined"
                size="large"
                sx={{ borderColor: 'divider', color: 'text.primary' }}
                onClick={() => goToRegisterTracked('hero_primary')}
              >
                Essayer Facteio gratuitement
              </ZenButton>
            </Stack>

            {/* MICRO-RASSURANCE */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
              alignItems="center"
              flexWrap="wrap"
              rowGap={2}
            >
              <Chip
                icon={<CheckCircle />}
                label="Conforme à la réforme 2026"
                variant="outlined"
                sx={{ borderColor: 'divider', color: 'text.primary', maxWidth: 260, textAlign: 'center' }}
              />
              <Chip
                icon={<EmojiPeople />}
                label="Pensé pour les indépendants"
                variant="outlined"
                sx={{ borderColor: 'divider', color: 'text.primary', maxWidth: 240, textAlign: 'center' }}
              />
              <Chip
                icon={<SupportAgent />}
                label="Assistance humaine disponible"
                variant="outlined"
                sx={{ borderColor: 'divider', color: 'text.primary', maxWidth: 260, textAlign: 'center' }}
              />
            </Stack>
          </Box>
        </Container>
      </HeroSection>

      {/* 🟨 SECTION PROBLÈME */}
      <Box sx={{ py: 10, background: (theme) => theme.palette.background.default }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <InfoOutlined sx={{ fontSize: 62, color: 'primary.main', mb: 2 }} />
            <Typography variant="h3" fontWeight={700} mb={2} color="text.primary">
              La facture électronique arrive…<br />
              et personne ne vous explique vraiment.
            </Typography>
          </Box>

          <ProblemCard>
            <CardContent>
              <Typography variant="h5" fontWeight={600} mb={3}>
                En 2026, la facture électronique devient obligatoire.<br />
                Mais aujourd'hui :
              </Typography>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <RemoveCircleOutline sx={{ color: '#8B8178', fontSize: 28, mt: '2px' }} />
                    <Typography variant="body1"><strong>les règles sont floues</strong></Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <RemoveCircleOutline sx={{ color: '#8B8178', fontSize: 28, mt: '2px' }} />
                    <Typography variant="body1"><strong>les plateformes sont complexes</strong></Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <RemoveCircleOutline sx={{ color: '#8B8178', fontSize: 28, mt: '2px' }} />
                    <Typography variant="body1"><strong>les explications sont incompréhensibles</strong></Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <RemoveCircleOutline sx={{ color: '#8B8178', fontSize: 28, mt: '2px' }} />
                    <Typography variant="body1"><strong>et une erreur peut coûter cher</strong></Typography>
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" fontWeight={600} color="#7A7068" mb={2}>
                👉 Résultat : stress, procrastination, inquiétude.
              </Typography>

              <Typography variant="body1" fontSize="1.1rem" color="text.secondary">
                Vous n'êtes pas seul.<br />
                Et surtout : <strong>ce n'est pas à vous de devenir expert.</strong>
              </Typography>
            </CardContent>
          </ProblemCard>
        </Container>
      </Box>

      {/* 🟩 SECTION SOLUTION */}
      <Box sx={{ py: 10, bgcolor: alpha('#EFE7DE', 0.92) }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Typography variant="h3" fontWeight={700} mb={2} color="#4F4741">
              Facteio s'occupe de l'essentiel.<br />
              Vous respirez. Vous facturez. Vous êtes prêt pour 2026.
            </Typography>
            <Typography variant="h6" color="text.secondary" maxWidth="800px" mx="auto">
              Facteio a été conçu pour une seule chose :<br />
              👉 <strong>vous simplifier la facture électronique.</strong>
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <FeatureBox>
                <CheckCircle sx={{ color: '#8B8178', fontSize: 32 }} />
                <Box>
                  <Typography variant="h6" fontWeight={600} mb={1}>
                    Création de devis et factures en quelques clics
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Interface intuitive, sans jargon technique
                  </Typography>
                </Box>
              </FeatureBox>
            </Grid>

            <Grid item xs={12} md={6}>
              <FeatureBox>
                <CheckCircle sx={{ color: '#8B8178', fontSize: 32 }} />
                <Box>
                  <Typography variant="h6" fontWeight={600} mb={1}>
                    Conforme à la réforme 2026
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Évolutions régulières selon les nouvelles règles
                  </Typography>
                </Box>
              </FeatureBox>
            </Grid>

            <Grid item xs={12} md={6}>
              <FeatureBox>
                <CheckCircle sx={{ color: '#8B8178', fontSize: 32 }} />
                <Box>
                  <Typography variant="h6" fontWeight={600} mb={1}>
                    Interface claire, sans jargon
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Compréhensible par tous, pas besoin d'être expert
                  </Typography>
                </Box>
              </FeatureBox>
            </Grid>

            <Grid item xs={12} md={6}>
              <FeatureBox>
                <CheckCircle sx={{ color: '#8B8178', fontSize: 32 }} />
                <Box>
                  <Typography variant="h6" fontWeight={600} mb={1}>
                    Support humain, quand vous en avez besoin
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Une vraie personne pour vous répondre
                  </Typography>
                </Box>
              </FeatureBox>
            </Grid>
          </Grid>

          <Box textAlign="center" mt={6}>
            <Typography variant="h5" fontWeight={600} mb={2} color="#4F4741">
              Pas une usine à gaz.<br />
              Pas une banque déguisée.<br />
              Juste ce qu'il faut. Bien fait.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* 🟦 SECTION POUR QUI */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="md">
          <Typography variant="h3" fontWeight={700} textAlign="center" mb={6} color="#4F4741">
            Facteio est fait pour vous si…
          </Typography>

          <SolutionCard sx={{ mb: 4 }}>
            <CardContent>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <CheckCircle sx={{ color: '#8B8178', fontSize: 28 }} />
                  <Typography variant="h6">Vous êtes indépendant, micro-entrepreneur ou TPE</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <CheckCircle sx={{ color: '#8B8178', fontSize: 28 }} />
                  <Typography variant="h6">L'administratif vous pèse</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <CheckCircle sx={{ color: '#8B8178', fontSize: 28 }} />
                  <Typography variant="h6">Vous voulez être en règle sans y passer vos soirées</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <CheckCircle sx={{ color: '#8B8178', fontSize: 28 }} />
                  <Typography variant="h6">Vous cherchez une solution claire, pas "tech"</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <CheckCircle sx={{ color: '#8B8178', fontSize: 28 }} />
                  <Typography variant="h6">Vous voulez quelqu'un qui vous explique simplement</Typography>
                </Box>
              </Stack>
            </CardContent>
          </SolutionCard>

          <Box sx={{ p: 3, bgcolor: alpha('#E7DED4', 0.92), borderRadius: 2, border: '2px solid #D8CEC3' }}>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <RemoveCircleOutline sx={{ color: '#8B8178' }} />
              <Typography variant="body1" fontWeight={600}>
                Ce n'est pas pour vous si vous aimez bidouiller des réglages complexes.
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* 🟪 SECTION ACCOMPAGNEMENT */}
      <Box sx={{ py: 10, bgcolor: alpha('#EFE7DE', 0.92) }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <SupportAgent sx={{ fontSize: 64, color: '#8B8178', mb: 2 }} />
            <Typography variant="h3" fontWeight={700} mb={2} color="#4F4741">
              Besoin d'aide pour la réforme 2026 ?<br />
              Je vous accompagne.
            </Typography>
          </Box>

          <Card sx={{ maxWidth: 700, mx: 'auto', p: 4, bgcolor: alpha('#F8F4EE', 0.94) }}>
            <CardContent>
              <Typography variant="body1" fontSize="1.1rem" mb={3}>
                Parce que chaque situation est différente,<br />
                Facteio propose aussi un <strong>accompagnement personnalisé</strong> (optionnel) :
              </Typography>

              <Stack spacing={2} mb={4}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <CheckCircle sx={{ color: '#8B8178' }} />
                  <Typography>explication claire de la réforme</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <CheckCircle sx={{ color: '#8B8178' }} />
                  <Typography>vérification de votre situation</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <CheckCircle sx={{ color: '#8B8178' }} />
                  <Typography>paramétrage si nécessaire</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <CheckCircle sx={{ color: '#8B8178' }} />
                  <Typography>réponses à vos questions, sans jargon</Typography>
                </Box>
              </Stack>

              <Typography variant="body1" fontWeight={600}>
                Vous n'êtes pas laissé seul face à un écran.
              </Typography>
            </CardContent>
          </Card>
        </Container>
      </Box>

      {/* 🟧 SECTION CONFIANCE */}
      <Box sx={{ py: 10, bgcolor: alpha('#F8F4EE', 0.94) }}>
        <Container maxWidth="md">
          <Box textAlign="center" mb={4}>
            <Box
              sx={{
                width: { xs: 200, sm: 230 },
                aspectRatio: '4 / 5',
                mx: 'auto',
                mb: 3,
                borderRadius: 4,
                overflow: 'hidden',
                bgcolor: '#5E564F',
                border: '3px solid #D8CEC3',
                boxShadow: '0px 14px 34px rgba(79, 71, 65, 0.14)'
              }}
            >
              <Box
                component="img"
                src={portraitSrc}
                alt="Sébastien"
                onError={() => setPortraitSrc(sebPortrait)}
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'block',
                  objectFit: 'cover',
                  objectPosition: 'center'
                }}
              />
            </Box>
            <Typography variant="h3" fontWeight={700} mb={3} color="#4F4741">
              Derrière Facteio, il y a une personne.
            </Typography>
          </Box>

          <Card sx={{ p: 4, bgcolor: '#FDFBF8' }}>
            <CardContent>
              <Typography variant="h5" fontWeight={600} mb={2}>
                Je m'appelle Sébastien.
              </Typography>
              <Typography variant="body1" fontSize="1.1rem" lineHeight={1.8} color="text.secondary">
                J'ai créé Facteio parce que, comme vous,
                je trouvais la facture électronique inutilement compliquée.
              </Typography>
              <Divider sx={{ my: 3 }} />
              <Typography variant="body1" fontSize="1.1rem" lineHeight={1.8}>
                Mon objectif n'est pas de vous enfermer
                dans un logiciel opaque,
                mais de <strong>vous aider à être prêt, serein et en règle.</strong>
              </Typography>
            </CardContent>
          </Card>
        </Container>
      </Box>

      {/* 🟩 SECTION TARIFS */}
      <Box sx={{ py: 10, bgcolor: alpha('#EFE7DE', 0.92) }}>
        <Container maxWidth="md" textAlign="center">
          <Typography variant="h3" fontWeight={700} mb={2} color="#4F4741">
            Un tarif simple. Sans surprise.
          </Typography>
          <Typography variant="h6" color="text.secondary" mb={4}>
            Abonnement mensuel clair • Sans engagement • Sans options cachées
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4, lineHeight: 1.7 }}>
            Offre fondateur : 14,99€ à vie pour les 100 premiers abonnés (prix bloqué).
          </Typography>

          <ZenButton
            variant="contained"
            size="large"
            sx={{ background: 'linear-gradient(135deg, #7A7068 0%, #8B8178 52%, #5E564F 100%)', color: '#fff' }}
            onClick={() => navigate('/pricing')}
          >
            Voir les tarifs
          </ZenButton>
        </Container>
      </Box>

      {/* 🟥 CALL TO ACTION FINAL */}
      <Box sx={{
        py: 12,
        background: 'linear-gradient(135deg, #4C433D 0%, #665A52 52%, #3F3833 100%)',
        color: 'white'
      }}>
        <Container maxWidth="md" textAlign="center">
          <Typography
            variant="h2"
            fontWeight={800}
            mb={4}
            sx={{
              color: '#FFFFFF',
              textShadow: '0 2px 8px rgba(0,0,0,0.35)',
              WebkitTextFillColor: '#FFFFFF'
            }}
          >
            Soyez prêt pour 2026, dès aujourd'hui.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'white',
                color: '#4F4741',
                px: 5,
                py: 2,
                fontSize: '1.2rem',
                fontWeight: 700,
                '&:hover': { bgcolor: '#f1f1f1' }
              }}
              onClick={() => goToRegisterTracked('footer_primary')}
            >
              Essayer Facteio gratuitement
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                borderColor: 'white',
                color: 'white',
                px: 5,
                py: 2,
                fontSize: '1.2rem',
                borderWidth: 2,
                '&:hover': { borderWidth: 2, bgcolor: alpha('#fff', 0.1) }
              }}
              onClick={() => navigate('/reforme-2026')}
            >
              Comprendre la réforme 2026
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* FOOTER */}
      <Box sx={{ py: 6, bgcolor: '#4F4741', color: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Typography variant="h5" fontWeight={700} mb={2} color="#C5A46D">
                 Facteio
              </Typography>
              <Typography variant="body2" color="rgba(255,255,255,0.92)">
                La facture électronique 2026, sans stress.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" fontWeight={700} mb={2} color="rgba(255,255,255,0.98)">Ressources</Typography>
              <Stack spacing={1}>
                <Button color="inherit" sx={{ color: 'rgba(255,255,255,0.92)', justifyContent: 'flex-start', textTransform: 'none' }} onClick={() => navigate('/reforme-2026')}>Réforme 2026</Button>
                <Button color="inherit" sx={{ color: 'rgba(255,255,255,0.92)', justifyContent: 'flex-start', textTransform: 'none' }} onClick={() => navigate('/faq')}>FAQ</Button>
                <Button color="inherit" sx={{ color: 'rgba(255,255,255,0.92)', justifyContent: 'flex-start', textTransform: 'none' }} onClick={() => navigate('/pricing')}>Tarifs</Button>
                <Button color="inherit" sx={{ color: 'rgba(255,255,255,0.92)', justifyContent: 'flex-start', textTransform: 'none' }} onClick={() => navigate('/facturation-btp')}>Facturation BTP</Button>
                <Button color="inherit" sx={{ color: 'rgba(255,255,255,0.92)', justifyContent: 'flex-start', textTransform: 'none' }} onClick={() => navigate('/facturation-beaute')}>Facturation Beauté</Button>
                <Button color="inherit" sx={{ color: 'rgba(255,255,255,0.92)', justifyContent: 'flex-start', textTransform: 'none' }} onClick={() => navigate('/logiciel-devis-facture-artisan')}>Logiciel Artisan BTP</Button>
                <Button color="inherit" sx={{ color: 'rgba(255,255,255,0.92)', justifyContent: 'flex-start', textTransform: 'none' }} onClick={() => navigate('/logiciel-facture-auto-entrepreneur')}>Logiciel Auto-Entrepreneur</Button>
                <Button color="inherit" sx={{ color: 'rgba(255,255,255,0.92)', justifyContent: 'flex-start', textTransform: 'none' }} onClick={() => navigate('/logiciel-facture-pme')}>Logiciel PME</Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="h6" fontWeight={700} mb={2} color="rgba(255,255,255,0.98)">Contact</Typography>
              <Typography variant="body2" color="rgba(255,255,255,0.92)">
                Email :{' '}
                <Link
                  href="mailto:sgdigitalweb13@gmail.com"
                  underline="hover"
                  sx={{ color: 'rgba(255,255,255,0.92)', fontWeight: 700 }}
                >
                  sgdigitalweb13@gmail.com
                </Link>
                <br />
                Une question ? Je vous réponds rapidement.
              </Typography>
            </Grid>
          </Grid>
          <Divider sx={{ my: 3, bgcolor: 'rgba(255,255,255,0.1)' }} />
          <Typography variant="body2" textAlign="center" color="rgba(255,255,255,0.5)">
            © 2026 Facteio - Créé par Sébastien avec <Favorite sx={{ fontSize: 16, color: '#C5A46D' }} />
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingNew;
